import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Home, ArrowLeft, ArrowRight, BookOpen, Sparkles, Target, HelpCircle, MessageCircle } from "lucide-react";
import { Seo } from "@/components/seo/Seo";
import { Helmet } from "react-helmet-async";
import { PublicShell } from "@/components/layouts/PublicShell";
import notFoundArt from "@/assets/404-illustration.jpg";

export default function NotFound() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    console.warn("404:", location.pathname);
  }, [location.pathname]);

  const popular = [
    { title: t("errors.notFound.popular.bootcamps.title"), desc: t("errors.notFound.popular.bootcamps.desc"), href: "/catalog", Icon: BookOpen },
    { title: t("errors.notFound.popular.services.title"), desc: t("errors.notFound.popular.services.desc"), href: "/services", Icon: Sparkles },
    { title: t("errors.notFound.popular.pricing.title"), desc: t("errors.notFound.popular.pricing.desc"), href: "/pricing", Icon: Target },
    { title: t("errors.notFound.popular.help.title"), desc: t("errors.notFound.popular.help.desc"), href: "/faq", Icon: HelpCircle },
  ];

  return (
    <PublicShell>
      <Seo title={t("errors.notFound.seoTitle", "Page not found — Bootpreneurs")} />
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="relative overflow-hidden">
        {/* Aurora */}
        <div className="pointer-events-none absolute inset-0 z-0">
          <div className="absolute -left-[10%] -top-[20%] h-[600px] w-[600px] animate-pulse rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.2), transparent 70%)" }} />
          <div className="absolute -bottom-[20%] -right-[10%] h-[600px] w-[600px] animate-pulse rounded-full opacity-25 blur-3xl"
            style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.15), transparent 70%)" }} />
        </div>
        <main className="relative z-10 mx-auto max-w-3xl px-6 pb-20 pt-10 text-center">
        <div className="mb-8 flex flex-col items-center">
          <img
            src={notFoundArt}
            alt=""
            aria-hidden
            width={420}
            height={420}
            className="h-auto w-[280px] md:w-[380px] drop-shadow-[0_24px_48px_hsl(var(--primary)/0.18)]"
            loading="eager"
            decoding="async"
          />
          <h1
            className="mt-2 text-7xl font-black leading-none tracking-tight md:text-8xl"
            style={{
              backgroundImage:
                "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.7), hsl(var(--accent, var(--primary))))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            404
          </h1>
        </div>
        <p className="mb-4 font-mono-jb text-xs uppercase tracking-[0.25em] text-muted-foreground">
          {t("errors.notFound.eyebrow", "Lost in transit")}
        </p>
        <h2 className="mb-5 text-3xl font-black tracking-tight text-foreground md:text-4xl">
          {t("errors.notFound.headline", "This page took a different route.")}
        </h2>
        <p className="mx-auto mb-10 max-w-xl text-base text-muted-foreground">
          {t("errors.notFound.sub", "Let's get you back on track — pick a destination below.")}
        </p>

        <div className="mb-16 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/" className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5">
            <Home size={16} /> {t("errors.notFound.home", "Back to home")}
          </Link>
          <button onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3 text-sm font-semibold text-foreground/80 hover:bg-muted">
            <ArrowLeft size={16} /> {t("errors.notFound.back", "Go back")}
          </button>
          <Link to="/contact"
            className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3 text-sm font-semibold text-foreground/80 hover:bg-muted">
            <MessageCircle size={16} /> {t("errors.notFound.contact", "Talk to an operator")}
          </Link>
        </div>

        <p className="mb-5 font-mono-jb text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {t("errors.notFound.popularH", "Popular destinations")}
        </p>
        <div className="mb-10 grid gap-3 text-left sm:grid-cols-2">
          {popular.map((p) => (
            <Link key={p.href} to={p.href}
              className="group flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                <p.Icon size={18} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-foreground">{p.title}</div>
                <div className="text-xs text-muted-foreground">{p.desc}</div>
              </div>
              <ArrowRight size={14} className="text-muted-foreground" />
            </Link>
          ))}
        </div>

        <div className="mx-auto max-w-md rounded-xl border border-border bg-card px-5 py-3 text-xs italic text-muted-foreground">
          {t("errors.notFound.stat", "Even the best maps need a redraw sometimes.")}
        </div>
        </main>
      </div>
    </PublicShell>
  );
}
